import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../admin.css';
import CategoryEditor from './CategoryEditor';
import SkillsEditor from './SkillsEditor';
import BlobsEditor from './BlobsEditor';
import { DEFAULT_BLOBS } from '../../constants/portfolioDefaults';
import staticExperienceDataset from '../../work_list/experienceData.json';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';

type ExperienceEntryData = {
  id: string;
  type: string;
  organisation: string;
  role: string;
  begin: string;
  end: string;
  description: string;
  descriptionVersions?: Record<string, string>;
  hiddenByDefault?: boolean;
  relatedWorks?: string[];
  tags?: string[];
};

type ExperienceDataset = {
  typeOrder: string[];
  entries: ExperienceEntryData[];
};

const EXPERIENCE_API_URL = 'http://localhost:3001/api/data-files/experienceData.json';

type ExperienceOverride = { hidden?: boolean; version?: string };

interface PortfolioRoutesEditorProps {
  initialContent: string;
  onContentChange: (newContent: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  saveStatus?: string | null;
}

// Helper to safely parse JSON
const safeParseJson = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null;
  }
};

// Helper to safely stringify JSON
const safeStringifyJson = (data: any) => {
  try {
    return JSON.stringify(data, null, 2);
  } catch (e) {
    console.error('Error stringifying JSON:', e);
    return '{}';
  }
};

const formatSmartCvSummary = (summary: any[]) => {
  if (!Array.isArray(summary)) return '';
  return summary.map(item => {
    if (Array.isArray(item)) {
      return item.map(sub => `- ${sub}`).join('\n');
    }
    return String(item);
  }).join('\n\n');
};

const parseSmartCvSummary = (text: string) => {
  const lines = text.split('\n');
  const result: any[] = [];

  let currentList: string[] = [];
  let currentTextBlock: string[] = [];

  const flushList = () => {
    if (currentList.length > 0) {
      result.push([...currentList]);
      currentList = [];
    }
  };

  const flushText = () => {
    if (currentTextBlock.length > 0) {
      result.push(currentTextBlock.join('\n'));
      currentTextBlock = [];
    }
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    // Empty line -> Separator (Flush everything, start new block)
    if (!trimmed) {
      flushList();
      flushText();
      continue;
    }

    // List Item
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      flushText(); // Switch from text to list
      currentList.push(trimmed.substring(2).trim());
    }
    // Regular Text
    else {
      flushList(); // Switch from list to text
      currentTextBlock.push(rawLine.trim());
    }
  }

  // Final flush
  flushList();
  flushText();

  return result;
};


// Default template for a new profile
const defaultNewProfileData = {
  siteTitle: "New Profile Title",
  sidebarTitle: "New Profile Sidebar",
  footer: {
    title: "",
    message: "",
    email: ""
  },
  cv: {
    link: "",
    showLink: true,
    experienceOverrides: {}
  },
  skills: undefined,
  home: {
    badge: "",
    title: "",
    intro: []
  },
  cvSummary: [],
  categories: {}
};


// CodeMirror 文字編輯器 — 解決游標跳動問題
const SmartTextEditor = ({
  value,
  onChange,
  placeholder,
  minHeight = '120px',
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  minHeight?: string;
}) => {
  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden', fontSize: '13.5px' }}>
      <CodeMirror
        value={value}
        theme={oneDark}
        extensions={[
          markdown(),
          EditorView.lineWrapping,
          EditorView.theme({
            '&': { minHeight },
            '.cm-scroller': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
            '.cm-placeholder': { color: '#6b7280', fontStyle: 'italic' },
          }),
        ]}
        placeholder={placeholder}
        onChange={onChange}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: true,
          foldGutter: false,
          autocompletion: false,
        }}
      />
    </div>
  );
};

const PortfolioRoutesEditor: React.FC<PortfolioRoutesEditorProps> = ({
  initialContent,
  onContentChange,
  onSave,
  isSaving,
  saveStatus
}) => {
  const [allData, setAllData] = useState<any>(() => safeParseJson(initialContent) || {});
  const [selectedProfileKey, setSelectedProfileKey] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [cvSummaryDraft, setCvSummaryDraft] = useState<string>('');
  const [homeIntroDraft, setHomeIntroDraft] = useState<string>('');
  const [dataVersion, setDataVersion] = useState(0);
  const lastSyncedRef = useRef<{ key: string; version: number }>({
    key: '',
    version: -1,
  });

  const lastSyncedContentRef = useRef<string>('');

  // Experience data lives in a separate file (experienceData.json). It's edited inline here
  // so you don't have to switch admin tabs, but it's saved independently of the route Save button.
  const [experienceDataset, setExperienceDataset] = useState<ExperienceDataset>(
    () => staticExperienceDataset as ExperienceDataset,
  );
  const [expandedExperienceIds, setExpandedExperienceIds] = useState<Set<string>>(new Set());
  const [isSavingExperience, setIsSavingExperience] = useState(false);
  const [experienceSaveStatus, setExperienceSaveStatus] = useState<string | null>(null);
  const [experienceError, setExperienceError] = useState<string | null>(null);

  useEffect(() => {
    // Refresh from the backend in case the bundled static JSON is stale; silently keep
    // the static fallback if the dev API isn't reachable (e.g. production build).
    fetch(EXPERIENCE_API_URL)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error('fetch failed'))))
      .then((text) => {
        const parsed = safeParseJson(text);
        if (parsed && Array.isArray(parsed.entries) && Array.isArray(parsed.typeOrder)) {
          setExperienceDataset(parsed);
        }
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  const updateExperienceEntries = useCallback(
    (updater: (entries: ExperienceEntryData[]) => ExperienceEntryData[]) => {
      setExperienceDataset((prev) => ({ ...prev, entries: updater(prev.entries) }));
    },
    [],
  );

  const updateExperienceField = useCallback(
    (id: string, field: keyof ExperienceEntryData, value: any) => {
      updateExperienceEntries((entries) =>
        entries.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)),
      );
    },
    [updateExperienceEntries],
  );

  const addExperienceVersionInline = useCallback(
    (id: string) => {
      const versionName = window.prompt('輸入版本名稱（例如 "data_insight"）：任何路徑都可以選用這個版本');
      const trimmed = versionName?.trim();
      if (!trimmed) return;
      updateExperienceEntries((entries) =>
        entries.map((entry) => {
          if (entry.id !== id) return entry;
          const existing = entry.descriptionVersions || {};
          if (existing[trimmed] !== undefined) {
            window.alert(`版本 "${trimmed}" 已經存在了，請直接編輯下方文字。`);
            return entry;
          }
          return { ...entry, descriptionVersions: { ...existing, [trimmed]: entry.description } };
        }),
      );
    },
    [updateExperienceEntries],
  );

  const updateExperienceVersionInline = useCallback(
    (id: string, versionName: string, value: string) => {
      updateExperienceEntries((entries) =>
        entries.map((entry) =>
          entry.id === id
            ? { ...entry, descriptionVersions: { ...(entry.descriptionVersions || {}), [versionName]: value } }
            : entry,
        ),
      );
    },
    [updateExperienceEntries],
  );

  const removeExperienceVersionInline = useCallback(
    (id: string, versionName: string) => {
      if (!window.confirm(`刪除版本 "${versionName}"？`)) return;
      updateExperienceEntries((entries) =>
        entries.map((entry) => {
          if (entry.id !== id) return entry;
          const next = { ...(entry.descriptionVersions || {}) };
          delete next[versionName];
          return { ...entry, descriptionVersions: next };
        }),
      );
    },
    [updateExperienceEntries],
  );

  const addNewExperienceEntry = useCallback(() => {
    const newEntry: ExperienceEntryData = {
      id: `entry_${Date.now()}`,
      type: experienceDataset.typeOrder[0] || 'Work Experience',
      organisation: 'New Organisation',
      role: '',
      begin: new Date().getFullYear().toString(),
      end: '',
      description: '',
      relatedWorks: [],
      tags: [],
    };
    updateExperienceEntries((entries) => [newEntry, ...entries]);
    setExpandedExperienceIds((prev) => new Set(prev).add(newEntry.id));
  }, [experienceDataset.typeOrder, updateExperienceEntries]);

  const removeExperienceEntry = useCallback(
    (id: string) => {
      if (!window.confirm('確定刪除這筆經歷？這會影響所有引用此經歷的路徑。')) return;
      updateExperienceEntries((entries) => entries.filter((entry) => entry.id !== id));
    },
    [updateExperienceEntries],
  );

  const toggleExpandedExperience = useCallback((id: string) => {
    setExpandedExperienceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const saveExperienceDataset = useCallback(async () => {
    setIsSavingExperience(true);
    setExperienceSaveStatus('Saving...');
    setExperienceError(null);
    try {
      const content = safeStringifyJson(experienceDataset);
      const response = await fetch(EXPERIENCE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as any));
        throw new Error(errorData.message || 'Failed to save experience data.');
      }
      setExperienceSaveStatus('Saved successfully!');
      setTimeout(() => setExperienceSaveStatus(null), 3000);
    } catch (err: any) {
      setExperienceError(err.message);
      setExperienceSaveStatus('Save failed.');
    } finally {
      setIsSavingExperience(false);
    }
  }, [experienceDataset]);

  useEffect(() => {
    // Prevent loop: if incoming content matches already synced, don't re-initialize
    if (initialContent === lastSyncedContentRef.current) return;

    const parsedData = safeParseJson(initialContent);
    if (parsedData) {
      setAllData(parsedData);
      setSelectedProfileKey((prevKey) => {
        if (prevKey && parsedData[prevKey]) {
          return prevKey;
        }
        const keys = Object.keys(parsedData);
        return keys.length ? keys[0] : '';
      });
      setDataVersion((version) => version + 1);
      setError(null);
      lastSyncedContentRef.current = initialContent;
    } else {
      setAllData({});
      setSelectedProfileKey('');
      setDataVersion((version) => version + 1);
      setError('Initial content is not valid JSON. Please correct it in the raw editor or add a new profile.');
    }
  }, [initialContent]);

  // Update parent when allData changes
  // IMPORTANT: also update lastSyncedContentRef here to prevent the loop:
  // setAllData → onContentChange → parent updates initialContent → useEffect[initialContent] fires
  // → setDataVersion → draft sync → setCvSummaryDraft → CodeMirror value resets → cursor jumps
  useEffect(() => {
    const newContent = safeStringifyJson(allData);
    lastSyncedContentRef.current = newContent;
    onContentChange(newContent);
  }, [allData, onContentChange]);

  const selectedProfileData = allData[selectedProfileKey];
  const defaultProfileData = allData['default'] || {};
  const profileKeys = Object.keys(allData);


  useEffect(() => {
    const shouldSync =
      lastSyncedRef.current.key !== selectedProfileKey ||
      lastSyncedRef.current.version !== dataVersion;

    if (!shouldSync) {
      return;
    }

    const profileData = allData[selectedProfileKey];
    if (!profileData) {
      setCvSummaryDraft('');
      setHomeIntroDraft('');
      return;
    }

    setCvSummaryDraft(formatSmartCvSummary(profileData.cvSummary || []));
    setHomeIntroDraft(
      Array.isArray(profileData.home?.intro)
        ? profileData.home.intro.join('\n\n')
        : '',
    );
    lastSyncedRef.current = { key: selectedProfileKey, version: dataVersion };
  }, [selectedProfileKey, dataVersion, allData]);


  const handleProfileChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProfileKey(e.target.value);
  }, []);

  const handleFieldChange = useCallback((profileKey: string, path: string[], value: any) => {
    setAllData((prevData: any) => {
      const newData = JSON.parse(JSON.stringify(prevData));
      let current = newData[profileKey];
      for (let i = 0; i < path.length - 1; i++) {
        if (current[path[i]] === undefined) current[path[i]] = {};
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      return newData;
    });
  }, []);

  const handleStringArrayChange = useCallback(
    (profileKey: string, path: string[], value: string) => {
      setHomeIntroDraft(value);
      const paragraphs = value.split(/\n\s*\n/);
      const formatted = paragraphs
        .map((block) => block.replace(/\r/g, '').split('\n').join('  \n')) // Preserve single newlines as Markdown br
        .filter((item) => item.trim().length > 0);
      handleFieldChange(profileKey, path, formatted);
    },
    [handleFieldChange],
  );

  const handleFooterChange = useCallback((profileKey: string, field: string, value: string) => {
    handleFieldChange(profileKey, ['footer', field], value);
  }, [handleFieldChange]);

  const handleCvChange = useCallback((profileKey: string, field: string, value: any) => {
    handleFieldChange(profileKey, ['cv', field], value);
  }, [handleFieldChange]);

  // Merge a patch into cv.experienceOverrides[entryId]. Removing keys that end up
  // matching the entry's own default keeps the saved JSON clean (no-op overrides aren't stored).
  const setExperienceOverride = useCallback(
    (profileKey: string, entryId: string, patch: Partial<ExperienceOverride>) => {
      setAllData((prevData: any) => {
        const newData = JSON.parse(JSON.stringify(prevData));
        const cv = newData[profileKey].cv || (newData[profileKey].cv = {});
        const overrides: Record<string, ExperienceOverride> = cv.experienceOverrides || {};
        const next: ExperienceOverride = { ...(overrides[entryId] || {}), ...patch };

        if (next.hidden === false) delete next.hidden;
        if (next.version === undefined || next.version === '') delete next.version;

        if (Object.keys(next).length === 0) {
          delete overrides[entryId];
        } else {
          overrides[entryId] = next;
        }
        cv.experienceOverrides = overrides;
        return newData;
      });
    },
    [],
  );

  const toggleExperienceHidden = useCallback(
    (profileKey: string, entryId: string, currentlyVisible: boolean) => {
      setExperienceOverride(profileKey, entryId, { hidden: currentlyVisible ? true : false });
    },
    [setExperienceOverride],
  );

  const setExperienceVersion = useCallback(
    (profileKey: string, entryId: string, versionName: string) => {
      setExperienceOverride(profileKey, entryId, { version: versionName || undefined });
    },
    [setExperienceOverride],
  );

  const handleAddProfile = useCallback(() => {
    const newKey = window.prompt('Enter a unique key for the new profile (e.g., "my_new_path"):');
    if (newKey && !profileKeys.includes(newKey)) {
      setAllData((prevData: any) => {
        const newData = { ...prevData, [newKey]: JSON.parse(JSON.stringify(defaultNewProfileData)) };
        return newData;
      });
      setSelectedProfileKey(newKey);
      setError(null);
    } else if (newKey) {
      setError(`Error: Profile key "${newKey}" already exists or is invalid.`);
    }
  }, [profileKeys]);

  const handleCopyProfile = useCallback(() => {
    if (!selectedProfileKey) return;
    const newKey = window.prompt(`Enter a unique key for the new profile (copy of "${selectedProfileKey}"):`);

    if (newKey && !profileKeys.includes(newKey)) {
      setAllData((prevData: any) => {
        const profileToCopy = JSON.parse(JSON.stringify(prevData[selectedProfileKey]));
        const newData = { ...prevData, [newKey]: profileToCopy };
        return newData;
      });
      setSelectedProfileKey(newKey);
      setError(null);
    } else if (newKey) {
      setError(`Error: Profile key "${newKey}" already exists or is invalid.`);
    }
  }, [profileKeys, selectedProfileKey]);

  const handleDeleteProfile = useCallback(() => {
    if (!selectedProfileKey) return;
    if (selectedProfileKey === 'default') {
      setError('Cannot delete the "default" profile.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete profile "${selectedProfileKey}"? This cannot be undone.`)) {
      const newAllData = { ...allData };
      delete newAllData[selectedProfileKey];
      setAllData(newAllData);
      const remainingKeys = Object.keys(newAllData);
      setSelectedProfileKey(remainingKeys.length > 0 ? remainingKeys[0] : '');
      setError(null);
    }
  }, [selectedProfileKey, allData]);


  if (profileKeys.length === 0) {
    return (
      <div className="editor-container">
        <h2>Portfolio Routes Editor</h2>
        {error && <p className="error-box">{error}</p>}
        <p>No profiles found. Click "Add Profile" to create one.</p>
        <button onClick={handleAddProfile} className="btn btn-primary">Add Profile</button>
      </div>
    );
  }

  return (
    <div className="editor-container">
      {error && <div className="error-box">{error}</div>}

      {/* Sticky Control Bar */}
      <div
        className="editor-controls"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#fff',
          borderBottom: '2px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '12px 16px',
          marginBottom: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label htmlFor="profile-select" className="editor-label" style={{ marginBottom: 0 }}>https://peien-portfolio.vercel.app/#/</label>
          <select
            id="profile-select"
            value={selectedProfileKey}
            onChange={handleProfileChange}
            className="editor-select"
            style={{ width: 'auto', minWidth: '200px' }}
          >
            {profileKeys.map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
          <button onClick={handleAddProfile} className="btn btn-secondary" style={{ padding: '6px 12px' }}>+ Add</button>
          {selectedProfileKey && (
            <>
              <button onClick={handleCopyProfile} className="btn btn-secondary" style={{ padding: '6px 12px' }}>Copy</button>
              <button
                onClick={handleDeleteProfile}
                className="btn btn-danger"
                disabled={selectedProfileKey === 'default'}
                style={{ padding: '6px 12px' }}
              >
                Delete
              </button>
            </>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Save Button */}
          {onSave && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {saveStatus && (
                <span style={{
                  color: saveStatus.includes('success') || saveStatus.includes('Saved') ? '#2e7d32' : '#1976d2',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}>
                  {saveStatus}
                </span>
              )}
              <button
                onClick={onSave}
                className="btn btn-primary"
                disabled={isSaving}
                style={{
                  padding: '8px 20px',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                {isSaving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedProfileData && (
        <>
          <div className="editor-section">
            <h3 className="editor-section-title">Profile: {selectedProfileKey}</h3>

            <div className="editor-grid-2">
              <div className="editor-col">
                <label className="editor-label">Site Title</label>
                <input
                  type="text"
                  className="editor-input"
                  value={selectedProfileData.siteTitle || ''}
                  placeholder={defaultProfileData.siteTitle || ''}
                  onChange={(e) => handleFieldChange(selectedProfileKey, ['siteTitle'], e.target.value)}
                />
              </div>
              <div className="editor-col">
                <label className="editor-label">Sidebar Title</label>
                <input
                  type="text"
                  className="editor-input"
                  value={selectedProfileData.sidebarTitle || ''}
                  placeholder={defaultProfileData.sidebarTitle || ''}
                  onChange={(e) => handleFieldChange(selectedProfileKey, ['sidebarTitle'], e.target.value)}
                />
              </div>
            </div>
            <div className="editor-grid-2" style={{ marginTop: '0.5rem' }}>
              <div className="editor-col">
                <label className="editor-label">Site Title (EN)</label>
                <input
                  type="text"
                  className="editor-input"
                  value={selectedProfileData.siteTitleEn || ''}
                  placeholder="English site title..."
                  onChange={(e) => handleFieldChange(selectedProfileKey, ['siteTitleEn'], e.target.value)}
                />
              </div>
              <div className="editor-col">
                <label className="editor-label">Sidebar Title (EN)</label>
                <input
                  type="text"
                  className="editor-input"
                  value={selectedProfileData.sidebarTitleEn || ''}
                  placeholder="English sidebar title..."
                  onChange={(e) => handleFieldChange(selectedProfileKey, ['sidebarTitleEn'], e.target.value)}
                />
              </div>
            </div>
            <div className="editor-row" style={{ marginTop: '1rem' }}>
              <label className="editor-label">語言 / Language</label>
              <select
                className="editor-select"
                value={selectedProfileData.lang || 'zh'}
                onChange={(e) => handleFieldChange(selectedProfileKey, ['lang'], e.target.value)}
                style={{ width: 'auto', minWidth: '200px' }}
              >
                <option value="zh">中文 (Chinese)</option>
                <option value="en">English (英文)</option>
              </select>
              <div className="editor-help-text" style={{ marginTop: '4px' }}>
                選擇「English」時，作品資訊將優先顯示英文欄位內容（含 Site Title EN / Sidebar Title EN）
              </div>
            </div>
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">Footer Content</h4>
            <div className="editor-grid-2" style={{ marginBottom: '1rem' }}>
              <div>
                <label className="editor-label">Footer Title</label>
                <input
                  type="text"
                  className="editor-input"
                  value={selectedProfileData.footer?.title || ''}
                  placeholder={defaultProfileData.footer?.title || ''}
                  onChange={(e) => handleFooterChange(selectedProfileKey, 'title', e.target.value)}
                />
              </div>
              <div>
                <label className="editor-label">Footer Email</label>
                <input
                  type="email"
                  className="editor-input"
                  value={selectedProfileData.footer?.email || ''}
                  placeholder={defaultProfileData.footer?.email || ''}
                  onChange={(e) => handleFooterChange(selectedProfileKey, 'email', e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="editor-label">Footer Message</label>
              <textarea
                className="editor-input"
                value={selectedProfileData.footer?.message || ''}
                placeholder={defaultProfileData.footer?.message || ''}
                onChange={(e) => handleFooterChange(selectedProfileKey, 'message', e.target.value)}
                rows={2}
                style={{ minHeight: '60px' }}
              />
            </div>
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">CV Settings</h4>
            <div className="editor-row">
              <label className="editor-label">CV Link (Google Drive/PDF URL)</label>
              <input
                type="url"
                className="editor-input"
                value={selectedProfileData.cv?.link || ''}
                placeholder={defaultProfileData.cv?.link || ''}
                onChange={(e) => handleCvChange(selectedProfileKey, 'link', e.target.value)}
              />
            </div>
            <div className="editor-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="cv-show-link"
                checked={selectedProfileData.cv?.showLink !== false}
                onChange={(e) => handleCvChange(selectedProfileKey, 'showLink', e.target.checked)}
              />
              <label htmlFor="cv-show-link" className="editor-label" style={{ marginBottom: 0 }}>顯示下載連結 (Show PDF Link)</label>
            </div>
            {(() => {
              const overrides: Record<string, ExperienceOverride> = selectedProfileData.cv?.experienceOverrides || {};
              return (
                <div className="editor-row" style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label className="editor-label" style={{ marginBottom: 0 }}>
                      CV 經歷顯示設定
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {experienceSaveStatus && (
                        <span style={{ fontSize: '0.8em', color: experienceSaveStatus.includes('failed') ? '#c62828' : '#2e7d32' }}>
                          {experienceSaveStatus}
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '2px 10px', fontSize: '0.8em' }}
                        onClick={addNewExperienceEntry}
                      >
                        + 新增經歷
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ padding: '2px 10px', fontSize: '0.8em' }}
                        onClick={saveExperienceDataset}
                        disabled={isSavingExperience}
                      >
                        💾 儲存經歷異動
                      </button>
                    </div>
                  </div>
                  <div className="editor-help-text" style={{ marginBottom: '8px' }}>
                    每筆經歷預設在所有路徑都會顯示，取消勾選即可在此路徑隱藏。點「編輯」可直接修改這筆經歷的內容與版本——這些變更存在 experienceData.json，與上方路徑設定是不同檔案，請用「儲存經歷異動」單獨儲存。
                  </div>
                  {experienceError && <div className="error-box" style={{ marginBottom: '8px' }}>{experienceError}</div>}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      maxHeight: '480px',
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      padding: '8px',
                    }}
                  >
                    {experienceDataset.entries.map((entry) => {
                      const override = overrides[entry.id];
                      const hiddenByDefault = Boolean(entry.hiddenByDefault);
                      const visible = !(override?.hidden ?? hiddenByDefault);
                      const versionNames = entry.descriptionVersions ? Object.keys(entry.descriptionVersions) : [];
                      const isExpanded = expandedExperienceIds.has(entry.id);

                      return (
                        <div
                          key={entry.id}
                          style={{
                            border: '1px solid ' + (isExpanded ? '#90caf9' : '#eee'),
                            borderRadius: '6px',
                            background: visible ? '#fff' : '#fafafa',
                          }}
                        >
                          <div
                            onClick={() => toggleExpandedExperience(entry.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '4px 6px',
                              opacity: visible ? 1 : 0.6,
                              fontSize: '0.85em',
                              cursor: 'pointer',
                            }}
                          >
                            <span style={{ color: '#999', width: '14px', flexShrink: 0 }}>
                              {isExpanded ? '▾' : '▸'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                              <input
                                type="checkbox"
                                checked={visible}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleExperienceHidden(selectedProfileKey, entry.id, visible)}
                              />
                              <span style={{ color: '#888', minWidth: '160px' }}>{entry.type}</span>
                              <span style={{ fontWeight: 600 }}>{entry.organisation}</span>
                              <span style={{ color: '#666' }}>{entry.role}</span>
                              {hiddenByDefault && (
                                <span style={{ color: '#b26a00', background: '#fff3e0', padding: '0 6px', borderRadius: '8px', fontSize: '0.85em' }}>
                                  預設隱藏
                                </span>
                              )}
                            </div>
                            {versionNames.length > 0 && (
                              <select
                                className="editor-select"
                                style={{ width: 'auto', fontSize: '0.85em', padding: '2px 6px' }}
                                value={override?.version || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setExperienceVersion(selectedProfileKey, entry.id, e.target.value)}
                              >
                                <option value="">（預設內容）</option>
                                {versionNames.map((versionName) => (
                                  <option key={versionName} value={versionName}>{versionName}</option>
                                ))}
                              </select>
                            )}
                          </div>

                          {isExpanded && (
                            <div style={{ padding: '10px 12px', borderTop: '1px solid #eee', background: '#fcfcfc' }}>
                              <div className="editor-grid-2">
                                <div className="editor-col">
                                  <label className="editor-label">Organisation</label>
                                  <input
                                    className="editor-input"
                                    value={entry.organisation}
                                    onChange={(e) => updateExperienceField(entry.id, 'organisation', e.target.value)}
                                  />
                                </div>
                                <div className="editor-col">
                                  <label className="editor-label">Role</label>
                                  <input
                                    className="editor-input"
                                    value={entry.role}
                                    onChange={(e) => updateExperienceField(entry.id, 'role', e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="editor-grid-2" style={{ marginTop: '8px' }}>
                                <div className="editor-col">
                                  <label className="editor-label">Type</label>
                                  <select
                                    className="editor-select"
                                    value={entry.type}
                                    onChange={(e) => updateExperienceField(entry.id, 'type', e.target.value)}
                                  >
                                    {experienceDataset.typeOrder.map((t) => (
                                      <option key={t} value={t}>{t}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="editor-col">
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <label className="editor-label">Begin</label>
                                      <input
                                        className="editor-input"
                                        value={entry.begin}
                                        onChange={(e) => updateExperienceField(entry.id, 'begin', e.target.value)}
                                        placeholder="YYYY/MM/DD"
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label className="editor-label">End</label>
                                      <input
                                        className="editor-input"
                                        value={entry.end}
                                        onChange={(e) => updateExperienceField(entry.id, 'end', e.target.value)}
                                        placeholder="YYYY/MM/DD or Present"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="editor-row" style={{ marginTop: '8px' }}>
                                <label className="editor-label">Description（預設版本）</label>
                                <textarea
                                  className="editor-textarea"
                                  rows={3}
                                  value={entry.description}
                                  onChange={(e) => updateExperienceField(entry.id, 'description', e.target.value)}
                                />
                              </div>

                              <div className="editor-row" style={{ marginTop: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                  <label className="editor-label" style={{ marginBottom: 0 }}>已儲存的版本</label>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ padding: '2px 10px', fontSize: '0.8em' }}
                                    onClick={() => addExperienceVersionInline(entry.id)}
                                  >
                                    + 新增版本
                                  </button>
                                </div>
                                {versionNames.length === 0 && (
                                  <div style={{ color: '#999', fontSize: '0.85em' }}>尚無已儲存的版本</div>
                                )}
                                {versionNames.map((versionName) => (
                                  <div
                                    key={versionName}
                                    style={{
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '6px',
                                      padding: '8px 10px',
                                      marginBottom: '8px',
                                      background: '#fafafa',
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                      <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: '0.85em', color: '#1565c0' }}>
                                        {versionName}
                                      </span>
                                      <button
                                        type="button"
                                        className="btn btn-danger"
                                        style={{ padding: '1px 8px', fontSize: '0.75em' }}
                                        onClick={() => removeExperienceVersionInline(entry.id, versionName)}
                                      >
                                        移除
                                      </button>
                                    </div>
                                    <textarea
                                      className="editor-textarea"
                                      rows={3}
                                      value={entry.descriptionVersions?.[versionName] || ''}
                                      onChange={(e) => updateExperienceVersionInline(entry.id, versionName, e.target.value)}
                                    />
                                  </div>
                                ))}
                              </div>

                              <div className="editor-row" style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9em' }}>
                                  <input
                                    type="checkbox"
                                    checked={hiddenByDefault}
                                    onChange={(e) => updateExperienceField(entry.id, 'hiddenByDefault', e.target.checked)}
                                  />
                                  預設在所有路徑隱藏
                                </label>
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  style={{ padding: '2px 10px', fontSize: '0.8em' }}
                                  onClick={() => removeExperienceEntry(entry.id)}
                                >
                                  刪除這筆經歷
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">CV Skills（技能編輯）</h4>
            <div className="editor-help-text" style={{ marginBottom: '1rem' }}>
              每個路徑可以使用全域技能或自訂技能。自訂技能僅影響此路徑的 CV 顯示。
            </div>
            <SkillsEditor
              skills={selectedProfileData.skills}
              onChange={(newSkills) => handleFieldChange(selectedProfileKey, ['skills'], newSkills)}
            />
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">Home Page Content</h4>
            <div className="editor-help-text" style={{ marginBottom: '1rem' }}>Use <b>double line breaks</b> to separate paragraphs.</div>

            <div className="editor-row">
              <label className="editor-label">Badge (Subtitle)</label>
              <input
                type="text"
                className="editor-input"
                value={selectedProfileData.home?.badge || ''}
                placeholder={defaultProfileData.home?.badge || ''}
                onChange={(e) => handleFieldChange(selectedProfileKey, ['home', 'badge'], e.target.value)}
              />
            </div>
            <div className="editor-row">
              <label className="editor-label">Title (Main Headline)</label>
              <input
                type="text"
                className="editor-input"
                value={selectedProfileData.home?.title || ''}
                placeholder={defaultProfileData.home?.title || ''}
                onChange={(e) => handleFieldChange(selectedProfileKey, ['home', 'title'], e.target.value)}
              />
            </div>
            <div className="editor-row">
              <label className="editor-label">Introduction</label>
              <SmartTextEditor
                value={homeIntroDraft}
                placeholder={"Write your intro here.\n\nNew paragraph."}
                onChange={(val) => handleStringArrayChange(selectedProfileKey, ['home', 'intro'], val)}
                minHeight="140px"
              />
            </div>
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">Home Blobs（首頁氣泡）</h4>
            <div className="editor-help-text" style={{ marginBottom: '1rem' }}>
              控制首頁右側的技能氣泡，可拖拽調整位置、調整大小、顏色與文字。每個路由可以有不同的氣泡配置。
              {(!selectedProfileData.blobs?.length && !selectedProfileData.home?.blobs?.length && selectedProfileKey !== 'default' && (defaultProfileData.blobs?.length > 0 || defaultProfileData.home?.blobs?.length > 0)) && (
                <div style={{ color: '#2563eb', marginTop: '0.4rem', fontWeight: 500 }}>
                  💡 當前正在沿用 default 路由的預設氣泡。若進行任何編輯並儲存，將會為此路由建立專屬的氣泡配置。
                </div>
              )}
            </div>
            <BlobsEditor
              blobs={
                (selectedProfileData.blobs && selectedProfileData.blobs.length > 0)
                  ? selectedProfileData.blobs
                  : (selectedProfileData.home?.blobs && selectedProfileData.home.blobs.length > 0)
                    ? selectedProfileData.home.blobs
                    : (selectedProfileKey !== 'default' && (defaultProfileData.blobs?.length > 0 || defaultProfileData.home?.blobs?.length > 0))
                      ? (defaultProfileData.blobs || defaultProfileData.home?.blobs)
                      : DEFAULT_BLOBS
              }
              onChange={(newBlobs) => handleFieldChange(selectedProfileKey, ['blobs'], newBlobs)}
            />
          </div>

          <div className="editor-section">
            <div className="editor-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>CV Summary</span>
              <span className="editor-help-text">Smart Editor</span>
            </div>
            <div className="editor-help-text" style={{ marginBottom: '1rem' }}>
              Format: <b>Double Enter</b> for new paragraph. <b>Single Enter</b> for line break. Start with <code>- </code> for bullets.
            </div>
            <SmartTextEditor
              value={cvSummaryDraft}
              placeholder={"peien.li@example.com\n\n**Experience**:\n- Job 1\n- Job 2"}
              onChange={(val) => {
                setCvSummaryDraft(val);
                const parsed = parseSmartCvSummary(val);
                handleFieldChange(selectedProfileKey, ['cvSummary'], parsed);
              }}
              minHeight="240px"
            />
          </div>

          <div className="editor-section">
            <h4 className="editor-section-title">Categories</h4>
            <CategoryEditor
              categories={selectedProfileData.categories || {}}
              onChange={(newCategories) => handleFieldChange(selectedProfileKey, ['categories'], newCategories)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PortfolioRoutesEditor;
